import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { WDSFilterFunction, WDSSelectionMode, WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import {
  FilterComponent,
  WizAgGrid,
  WizDatasource,
  WizRoot,
  WizEvent,
  WizEventManager, 
} from '@wizeoo/akm-component-lib';
import { AlkEvent } from '../../framework/events/alk.event';
import { ALK_EVENT_TYPE } from '../../model/enums';
import {
  FilterConfig,
  GridConfig,
  ICON_TYPE,
  ListConfig,
  MODE,
  MultiDisplayConfig,
  TileConfig,
  TreeConfig,
} from './config/multi-display.config';


const GRID_ICON: ICON_TYPE = { code: 'grid', icon: 'icon-view-grid' };
const TREE_ICON: ICON_TYPE = { code: 'tree', icon: 'icon-view-treemap' };
const LIST_ICON: ICON_TYPE = { code: 'list', icon: 'icon-view-list' };
const TILE_ICON: ICON_TYPE = { code: 'tiles', icon: 'icon-view-thumbnail' };

@Component({
  selector: 'multi-display',
  templateUrl: './multi-display.component.html',
  styleUrls: ['./multi-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiDisplay<T> implements AfterViewInit, OnDestroy {
  @ViewChild('listTpl', { read: TemplateRef, static: true }) listTemplate: TemplateRef<any>;
  @ViewChild('gridTpl', { read: TemplateRef, static: true }) gridTemplate: TemplateRef<any>;
  @ViewChild('treeTpl', { read: TemplateRef, static: true }) treeTemplate: TemplateRef<any>;
  @ViewChild('tilesTpl', { read: TemplateRef, static: true }) tileTemplate: TemplateRef<any>;
  @ViewChild('filter', { read: FilterComponent, static: false }) filter: FilterComponent;
  @ViewChild('listContainer', { read: ElementRef, static: true }) listContainer: ElementRef;

  @ViewChild('agGrid') public agGrid: WizAgGrid<any>;

  /**
   * Les modes disponibles
   */
  modes: WizDatasource<ICON_TYPE> = new WizDatasource([GRID_ICON, TREE_ICON, LIST_ICON, TILE_ICON])
    .setFilter(this.modesFilter.bind(this))
    .build();

  @Input() public code: string = null; // le code pour permettre la coexistance de plusieurs md sur une même vue

  /**
   * Méthode de filtre sur les modes
   */
  private modesFilter(item: ICON_TYPE): boolean {
    return (
      this._config &&
      this._config[item.code] !== undefined &&
      this.config[item.code] !== null &&
      this._config[item.code].enabled
    );
  }


  private _config: MultiDisplayConfig<T>;
  /**
   * Défini la configuration du composant
   */
  @Input() public set config(value: MultiDisplayConfig<T>) {
    this._config = value;
    this.modes.refresh();
    this.currentMode = this._context?.getMultiDisplayMode(this) || this._config.defaultMode; // si pas 1ere vue

    this.cdref.detectChanges();
  }

  public get config(): MultiDisplayConfig<T> {
    return this._config;
  }

  /**
   * collecte de la conf du multiDisplay
   */
  private get _context(): any { // any pour éviter les deps circulaires
    return this._config.model?.userContextService || null;
  }

  constructor(private cdref: ChangeDetectorRef, public elementRef: ElementRef) {}

  /**
   * On doit garder la référence à la fonction de filtre car on bind this sur la fonction
   * La fonction de filtre est retirée de la datatsource sur le destroy
   */
  private _filterRef: WDSFilterFunction<T>;

  ngAfterViewInit() {
    // On a des filtres, on lance la construction
    if (this.hasFilter) {
      this._config.filter.initFilter(this.filter);
      this._filterRef = this.doFilter.bind(this);
      this.datasource.addFilter(this._filterRef);
      // Sur changement dans la zone de filtre, on refresh
      WizEventManager.addListener(this, this.filter, WIZ_EVENT_TYPE.FILTER, () => {
        this.datasource.refresh();
        this._context?.saveLocalFilter(this.filter);
      });
    }

    if (!!this._config.onSelectionChanging) {
      WizEventManager.addListener(
        this,
        this.listContainer,
        WIZ_EVENT_TYPE.SELECTION_CHANGING,
        this.onSelectionChanging,
        { capture: true }
      );
    }

    if (!!this._config.onSelectionChanged) {
      WizEventManager.addListener(this, this.listContainer, WIZ_EVENT_TYPE.SELECTION_CHANGED, this.onSelectionChanged);
    }

    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.TAB_IS_ON, this.setMode);
  }


  /** 1ère vue de l'appli, refresh du mode */
  private setMode(e: AlkEvent): void {
    // prise du mode, la vue est up
    // console.log('md view Init', this.code);
    this.currentMode = this._context?.getMultiDisplayMode(this) || this._config.defaultMode; 
  }

  ngOnDestroy() {
    // Si on avait des filtres, on supprime l'event et on enlève la filter func de la datasource
    if (this.hasFilter) {
      WizEventManager.removeListener(this, this.filter, WIZ_EVENT_TYPE.FILTER);
      this.datasource.removeFilter(this._filterRef);
    }

    if (!!this._config.onSelectionChanging) {
      WizEventManager.removeListener(this, this.listContainer, WIZ_EVENT_TYPE.SELECTION_CHANGING);
    }

    if (!!this._config.onSelectionChanged) {
      WizEventManager.removeListener(this, this.listContainer, WIZ_EVENT_TYPE.SELECTION_CHANGED);
    }

    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.TAB_IS_ON);
  }

  /**
   * Transmission de l'event de changement de selection en cours
   * @param pEvent
   */
  protected onSelectionChanging(pEvent: WizEvent): void {
    this._config.onSelectionChanging(pEvent);
  }

  /**
   * Transmission de l'event de changement de selection
   * @param pEvent
   */
  protected onSelectionChanged(pEvent: WizEvent): void {
    this._config.onSelectionChanged(pEvent);
  }

  /**
   * Filtre de la datasource
   *
   * @param pItem L'élément à filtrer
   */
  protected doFilter(pItem: T): boolean {
    // on s'en remet au composant de filtre pour valider la visibilité
    return this.filter.checkVisible(pItem);
  }

  /**
   * Le mode en cours d'affichage
   */
  currentMode: MODE;

  /**
   * Changement de mode
   */
  changeMode(multiDisplayMode: MODE): void {
    if (multiDisplayMode === this.currentMode) {
      return;
    }
    this.currentMode = multiDisplayMode;
    this._context?.saveMultiDisplayMode(this);
  }

  get datasource(): WizDatasource<T> {
    return this._config ? this._config.datasource : null;
  }

  /**
   * La configuration pour les tiles
   */
  get tileConfig(): TileConfig {
    return this._config.tiles;
  }

  /**
   * La configuration du grid
   */
  get gridConfig(): GridConfig {
    return this._config.grid;
  }

  get gridRowSelection(): WDSSelectionMode {
    return this.gridConfig?.rowSelection || 'single';
  }

  get shouldSuppressGridClick(): boolean {
    return !!this.gridConfig.suppressRowClickSelection ? true : false;
  }

  /**
   * La configuration du tree
   */
  get treeConfig(): TreeConfig {
    return this._config.tree;
  }

  /**
   * La configuration de la liste
   */
  get listConfig(): ListConfig {
    return this._config.list;
  }

  /**
   * La config du composant de filtre
   */
  get filterConfig(): FilterConfig {
    return this._config.filter;
  }

  /**
   * La largeur du composant de filtre. Par défaut, 100%
   */
  get filterWidth(): string {
    return !!this.filterConfig.width ? this.filterConfig.width : '100%';
  }

  /**
   * Indique si une configuration de filtre a été donnée
   */
  get hasFilter(): boolean {
    return !!this._config && !!this._config.filter && this._config.filter.enabled;
  }

  /**
   * Accés au template à afficher en fonction du mode choisi
   */
  get template(): TemplateRef<any> {
    switch (this.currentMode) {
      case 'grid':
        return this.gridTemplate;
      case 'list':
        return this.listTemplate;
      case 'tree':
        return this.treeTemplate;
      case 'tiles':
        return this.tileTemplate;
    }
  }
}
